<template>
  <div class="room-list-container">
    <!-- Header -->
    <div class="page-header mb-4">
      <el-row :gutter="20" align="middle">
        <el-col :span="12">
          <h2 class="page-title">
            <i class="mdi mdi-door"></i>
            Room Management
          </h2>
          <p class="page-subtitle">Manage property rooms and availability</p>
        </el-col>
        <el-col :span="12" class="text-right">
          <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog = true">
            Add Room
          </el-button>
          <el-button icon="el-icon-download" @click="exportRooms">
            Export
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- Room Stats -->
    <el-row :gutter="20" class="mb-4">
      <el-col :xs="24" :sm="8" :md="4" v-for="stat in roomStats" :key="stat.label">
        <el-card class="stat-card">
          <div class="stat-content">
            <div :class="['stat-icon', stat.class]">
              <i :class="stat.icon"></i>
            </div>
            <div class="stat-info">
              <h3>{{ stat.value }}</h3>
              <p>{{ stat.label }}</p>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Filters -->
    <el-card class="filter-card mb-4">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="Floor">
          <el-select v-model="filters.floor" placeholder="All Floors" clearable @change="fetchRooms">
            <el-option label="All Floors" value=""></el-option>
            <el-option label="1st Floor" :value="1"></el-option>
            <el-option label="2nd Floor" :value="2"></el-option>
            <el-option label="3rd Floor" :value="3"></el-option>
            <el-option label="4th Floor" :value="4"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Room Type">
          <el-select v-model="filters.room_type" placeholder="All Types" clearable @change="fetchRooms">
            <el-option label="All Types" value=""></el-option>
            <el-option
              v-for="type in roomTypes"
              :key="type.name"
              :label="type.name"
              :value="type.name"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Status">
          <el-select v-model="filters.status" placeholder="All Statuses" clearable @change="fetchRooms">
            <el-option label="All Statuses" value=""></el-option>
            <el-option label="Available" value="available"></el-option>
            <el-option label="Occupied" value="occupied"></el-option>
            <el-option label="Reserved" value="reserved"></el-option>
            <el-option label="Maintenance" value="maintenance"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Housekeeping">
          <el-select v-model="filters.housekeeping" placeholder="All" clearable @change="fetchRooms">
            <el-option label="All" value=""></el-option>
            <el-option label="Clean" value="clean"></el-option>
            <el-option label="Cleaning" value="cleaning"></el-option>
            <el-option label="Dirty" value="dirty"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-refresh" @click="resetFilters">Reset</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Room Grid View -->
    <div class="view-toggle mb-3">
      <el-radio-group v-model="viewMode" size="small">
        <el-radio-button label="grid">
          <i class="el-icon-s-grid"></i> Grid View
        </el-radio-button>
        <el-radio-button label="list">
          <i class="el-icon-tickets"></i> List View
        </el-radio-button>
      </el-radio-group>
    </div>

    <!-- Grid View -->
    <div v-if="viewMode === 'grid'" class="room-grid" v-loading="loading">
      <el-row :gutter="20">
        <el-col
          v-for="room in rooms"
          :key="room.id"
          :xs="24" :sm="12" :md="8" :lg="6"
          class="mb-4"
        >
          <el-card :body-style="{ padding: '0px' }" class="room-card" :class="'status-' + room.status">
            <div class="room-image">
              <el-image
                :src="room.images[0] || '/assets/img/rooms/default-room.jpg'"
                fit="cover"
                style="width: 100%; height: 180px"
              >
                <div slot="error" class="image-slot">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </el-image>
              <div class="room-status-badge">
                <el-tag :type="getRoomStatusType(room.status)" effect="dark" size="mini">
                  {{ room.status }}
                </el-tag>
              </div>
            </div>

            <div class="room-info">
              <h3>Room {{ room.room_number }}</h3>
              <p class="room-type">{{ room.room_type }}</p>
              <div class="room-details">
                <span><i class="mdi mdi-account-multiple"></i> {{ room.max_occupancy }} Guests</span>
                <span><i class="mdi mdi-currency-usd"></i> {{ room.base_price }}/night</span>
              </div>
              <div class="room-amenities">
                <el-tag
                  v-for="(amenity, index) in room.amenities.slice(0, 3)"
                  :key="index"
                  size="mini"
                  type="info"
                >
                  {{ amenity }}
                </el-tag>
                <el-tag v-if="room.amenities.length > 3" size="mini" type="info">
                  +{{ room.amenities.length - 3 }}
                </el-tag>
              </div>
              <div class="room-housekeeping">
                <el-tag :type="getHousekeepingType(room.housekeeping_status)" size="mini">
                  <i class="mdi mdi-broom"></i> {{ room.housekeeping_status }}
                </el-tag>
              </div>
            </div>

            <div class="room-actions">
              <el-button type="text" @click="viewRoom(room)">View</el-button>
              <el-button type="text" @click="editRoom(room)">Edit</el-button>
              <el-dropdown trigger="click">
                <el-button type="text">
                  More <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click="changeStatus(room)">
                    <i class="el-icon-refresh"></i> Change Status
                  </el-dropdown-item>
                  <el-dropdown-item @click="updateHousekeeping(room)">
                    <i class="mdi mdi-broom"></i> Update Housekeeping
                  </el-dropdown-item>
                  <el-dropdown-item @click="scheduleMaintenance(room)" divided>
                    <i class="el-icon-setting"></i> Schedule Maintenance
                  </el-dropdown-item>
                  <el-dropdown-item @click="deleteRoom(room)">
                    <i class="el-icon-delete"></i> Delete
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- List View -->
    <el-card v-else>
      <el-table :data="rooms" v-loading="loading" style="width: 100%">
        <el-table-column prop="room_number" label="Room No." width="100" fixed>
          <template slot-scope="scope">
            <router-link :to="`/rooms/${scope.row.id}`" class="room-link">
              {{ scope.row.room_number }}
            </router-link>
          </template>
        </el-table-column>

        <el-table-column prop="room_type" label="Type" width="150"></el-table-column>

        <el-table-column prop="floor" label="Floor" width="80" align="center"></el-table-column>

        <el-table-column prop="max_occupancy" label="Capacity" width="100" align="center">
          <template slot-scope="scope">
            <i class="mdi mdi-account-multiple"></i> {{ scope.row.max_occupancy }}
          </template>
        </el-table-column>

        <el-table-column prop="base_price" label="Price/Night" width="120" align="right">
          <template slot-scope="scope">
            ${{ scope.row.base_price }}
          </template>
        </el-table-column>

        <el-table-column prop="amenities" label="Amenities" min-width="200">
          <template slot-scope="scope">
            <el-tag
              v-for="(amenity, index) in scope.row.amenities"
              :key="index"
              size="mini"
              type="info"
              style="margin-right: 5px"
            >
              {{ amenity }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="Status" width="120" align="center">
          <template slot-scope="scope">
            <el-tag :type="getRoomStatusType(scope.row.status)" effect="dark">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="housekeeping_status" label="Housekeeping" width="120" align="center">
          <template slot-scope="scope">
            <el-tag :type="getHousekeepingType(scope.row.housekeeping_status)">
              {{ scope.row.housekeeping_status }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Actions" width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="editRoom(scope.row)">Edit</el-button>
            <el-dropdown trigger="click" style="margin-left: 10px">
              <el-button type="text" size="small">
                More <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item @click="changeStatus(scope.row)">Change Status</el-dropdown-item>
                <el-dropdown-item @click="updateHousekeeping(scope.row)">Update Housekeeping</el-dropdown-item>
                <el-dropdown-item @click="deleteRoom(scope.row)" divided>Delete</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Create/Edit Room Dialog -->
    <create-room-dialog
      :visible.sync="showCreateDialog"
      :room="selectedRoom"
      :room-types="roomTypes"
      @saved="onRoomSaved"
    />

    <!-- Room Status Dialog -->
    <room-status-dialog
      :visible.sync="showStatusDialog"
      :room="selectedRoom"
      @updated="onStatusUpdated"
    />

    <!-- Housekeeping Dialog -->
    <housekeeping-dialog
      :visible.sync="showHousekeepingDialog"
      :room="selectedRoom"
      @updated="onHousekeepingUpdated"
    />
  </div>
</template>

<script>
import roomService from '@/services/room.service'
import CreateRoomDialog from './Modals/CreateRoomDialog.vue'
import RoomStatusDialog from './Modals/RoomStatusDialog.vue'
import HousekeepingDialog from './Modals/HousekeepingDialog.vue'

export default {
  name: 'RoomList',
  components: {
    CreateRoomDialog,
    RoomStatusDialog,
    HousekeepingDialog
  },
  data () {
    return {
      rooms: [],
      loading: false,
      viewMode: 'grid',
      filters: {
        floor: '',
        room_type: '',
        status: '',
        housekeeping: ''
      },
      selectedRoom: null,
      showCreateDialog: false,
      showStatusDialog: false,
      showHousekeepingDialog: false,
      roomTypes: [],
      stats: {
        total_rooms: 0,
        available_rooms: 0,
        occupied_rooms: 0,
        maintenance_rooms: 0,
        reserved_rooms: 0,
        occupancy_rate: 0
      }
    }
  },
  computed: {
    roomStats () {
      return [
        {
          label: 'Total Rooms',
          value: this.stats.total_rooms,
          icon: 'mdi mdi-door',
          class: 'primary'
        },
        {
          label: 'Available',
          value: this.stats.available_rooms,
          icon: 'mdi mdi-check-circle',
          class: 'success'
        },
        {
          label: 'Occupied',
          value: this.stats.occupied_rooms,
          icon: 'mdi mdi-account-check',
          class: 'warning'
        },
        {
          label: 'Reserved',
          value: this.stats.reserved_rooms,
          icon: 'mdi mdi-calendar-clock',
          class: 'info'
        },
        {
          label: 'Maintenance',
          value: this.stats.maintenance_rooms,
          icon: 'mdi mdi-tools',
          class: 'danger'
        },
        {
          label: 'Occupancy Rate',
          value: `${this.stats.occupancy_rate}%`,
          icon: 'mdi mdi-percent',
          class: 'purple'
        }
      ]
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('room.title'))
    try {
      await this.withLoading(async () => {
        const data = await this.$roomService.getRooms()
        this.rooms = data
      }, 'loading')
    } catch (error) {
      // Error is already handled by the global interceptor
      console.log('Component caught error in getRooms:', error)
    }
    this.fetchRoomTypes()
    this.fetchStats()
  },
  methods: {
    async fetchRooms () {
      this.loading = true
      try {
        const response = await roomService.getRooms(this.filters)
        this.rooms = response.data.data
      } catch (error) {
        this.$message.error('Failed to load rooms')
      } finally {
        this.loading = false
      }
    },

    async fetchRoomTypes () {
      try {
        const response = await this.$roomService.getRoomTypes()
        this.roomTypes = response.data
      } catch (error) {
        console.error('Failed to load room types:', error)
      }
    },

    async fetchStats () {
      try {
        const response = await this.$roomService.getRoomStats()
        this.stats = response.data
      } catch (error) {
        console.error('Failed to load room stats:', error)
      }
    },

    resetFilters () {
      this.filters = {
        floor: '',
        room_type: '',
        status: '',
        housekeeping: ''
      }
      this.fetchRooms()
    },

    getRoomStatusType (status) {
      const types = {
        available: 'success',
        occupied: 'warning',
        reserved: 'primary',
        maintenance: 'danger'
      }
      return types[status] || 'info'
    },

    getHousekeepingType (status) {
      const types = {
        clean: 'success',
        cleaning: 'warning',
        dirty: 'danger'
      }
      return types[status] || 'info'
    },

    viewRoom (room) {
      this.$router.push(`/rooms/${room.id}`)
    },

    editRoom (room) {
      this.selectedRoom = room
      this.showCreateDialog = true
    },

    changeStatus (room) {
      this.selectedRoom = room
      this.showStatusDialog = true
    },

    updateHousekeeping (room) {
      this.selectedRoom = room
      this.showHousekeepingDialog = true
    },

    scheduleMaintenance (room) {
      this.$router.push(`/rooms/${room.id}/maintenance`)
    },

    async deleteRoom (room) {
      try {
        await this.$confirm(`Are you sure you want to delete Room ${room.room_number}?`, 'Delete Room', {
          confirmButtonText: 'Yes, Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        await roomService.deleteRoom(room.id)
        this.$message.success('Room deleted successfully')
        this.fetchRooms()
        this.fetchStats()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('Failed to delete room')
        }
      }
    },

    onRoomSaved () {
      this.showCreateDialog = false
      this.selectedRoom = null
      this.fetchRooms()
      this.fetchStats()
    },

    onStatusUpdated () {
      this.showStatusDialog = false
      this.selectedRoom = null
      this.fetchRooms()
      this.fetchStats()
    },

    onHousekeepingUpdated () {
      this.showHousekeepingDialog = false
      this.selectedRoom = null
      this.fetchRooms()
    },

    exportRooms () {
      this.$message.info('Export feature coming soon')
    }
  }
}
</script>

<style scoped>
.room-list-container {
  padding: 20px;
}

.page-header {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.page-title {
  margin: 0;
  font-size: 24px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title i {
  color: #409EFF;
}

.page-subtitle {
  margin: 5px 0 0;
  color: #909399;
  font-size: 14px;
}

/* Stat Cards */
.stat-card {
  height: 100%;
  transition: all 0.3s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-content {
  display: flex;
  align-items: center;
  padding: 10px;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  margin-right: 15px;
}

.stat-icon.primary { background: linear-gradient(135deg, #409EFF, #1E88E5); }
.stat-icon.success { background: linear-gradient(135deg, #67C23A, #43A047); }
.stat-icon.warning { background: linear-gradient(135deg, #E6A23C, #F57C00); }
.stat-icon.info { background: linear-gradient(135deg, #909399, #616161); }
.stat-icon.danger { background: linear-gradient(135deg, #F56C6C, #E53935); }
.stat-icon.purple { background: linear-gradient(135deg, #9C27B0, #7B1FA2); }

.stat-info h3 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.stat-info p {
  margin: 2px 0 0;
  color: #909399;
  font-size: 13px;
}

/* Filter Card */
.filter-card {
  margin-bottom: 20px;
}

.view-toggle {
  text-align: right;
}

/* Room Grid */
.room-grid {
  min-height: 400px;
}

.room-card {
  height: 100%;
  transition: all 0.3s;
  cursor: pointer;
}

.room-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.room-image {
  position: relative;
}

.room-status-badge {
  position: absolute;
  top: 10px;
  right: 10px;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 40px;
}

.room-info {
  padding: 15px;
}

.room-info h3 {
  margin: 0 0 5px;
  font-size: 18px;
  color: #303133;
}

.room-type {
  margin: 0 0 10px;
  color: #909399;
  font-size: 14px;
}

.room-details {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #606266;
}

.room-details span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.room-amenities {
  margin-bottom: 10px;
}

.room-amenities .el-tag {
  margin-right: 5px;
  margin-bottom: 5px;
}

.room-housekeeping {
  margin-top: 10px;
}

.room-actions {
  border-top: 1px solid #EBEEF5;
  padding: 10px 15px;
  display: flex;
  justify-content: space-around;
}

/* List View */
.room-link {
  color: #409EFF;
  text-decoration: none;
  font-weight: 500;
}

.room-link:hover {
  text-decoration: underline;
}

.text-right {
  text-align: right;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-3 {
  margin-bottom: 12px;
}

/* Room Status Colors */
.status-maintenance {
  border-top: 3px solid #F56C6C;
}

.status-occupied {
  border-top: 3px solid #E6A23C;
}

.status-available {
  border-top: 3px solid #67C23A;
}

.status-reserved {
  border-top: 3px solid #409EFF;
}
</style>
